import { cloneDeep } from 'lodash';

import {
  combineChannels,
  compareCountriesDSPs,
  getChannelsWithCategory,
  addPriorityPlaylisting,
} from './helpers';
import {
  EventCategories,
  IEventChannel,
  ISubscription,
} from 'redux/reducers/subscriptions';

import { eventCategoryLabels } from '../typeToName';

/** Get subscriptions array ready to save
 * If we have existing subscriptions, we compare new ones with existing ones and merge them, if they are similar, or save separately, if they are different
 */
export const getSubscriptions = ({
  newSubscriptions,
  subscriptions,
}: {
  newSubscriptions: ISubscription[];
  subscriptions: ISubscription[];
}): ISubscription[] => {
  let combinedSubscriptions;
  const newSubscriptionsCopy = cloneDeep(newSubscriptions);

  /** Sync exiting and new subscriptions to re-write */
  if (subscriptions && subscriptions.length > 0) {
    combinedSubscriptions = newSubscriptions.reduce(
      (prev: any, event: ISubscription, index: number) => {
        subscriptions.forEach((subscription: ISubscription) => {
          /** Check if we have subscriptions with the same countries and dsps in existing and new subscriptions */
          const hasSameCountryAndCustomer = compareCountriesDSPs(
            subscription,
            event
          );

          if (hasSameCountryAndCustomer) {
            const combinedSubscriptionChannels = Object.keys(
              eventCategoryLabels
            ).reduce((allChannels: any, initialCategory: EventCategories) => {
              // if we have category in a new subscription, it means that we should re-write events for this category
              const newCategoryChannels = getChannelsWithCategory({
                item: event,
                category: initialCategory,
              });

              /** If we have a category in an old subscription and do not have it in a new one,
               * We need to keep the category in a combined subscription.
               */
              const oldCategoryChannels = getChannelsWithCategory({
                item: subscription,
                category: initialCategory,
              });

              if (newCategoryChannels.length > 0) {
                combineChannels({
                  categoryChannels: newCategoryChannels,
                  all: allChannels,
                  category: initialCategory,
                  hasPriorityPlaylists: newCategoryChannels.some(
                    (channel: IEventChannel) =>
                      channel.hasOwnProperty('eventTypes')
                  ),
                });
              } else if (oldCategoryChannels.length > 0) {
                combineChannels({
                  categoryChannels: oldCategoryChannels,
                  all: allChannels,
                  category: initialCategory,
                  hasPriorityPlaylists: oldCategoryChannels.some(
                    (channel: IEventChannel) =>
                      channel.hasOwnProperty('eventTypes')
                  ),
                });
              }

              return allChannels;
            }, []);

            prev.push({
              countryId: event.countryId,
              customerId: event.customerId,
              eventChannels: combinedSubscriptionChannels,
            });

            /** If a new subscription is merged with the existing one, it will not need to save it separately,
             * so we change it to undefined to remove it easily from the list of new subscriptions for saving */
            newSubscriptionsCopy.splice(index, 1, undefined as any);
          }
        });
        return prev;
      },
      []
    );
  }

  /** Remove undefined elements to clear the list of new subscriptions and prepare it for saving */
  const filteredCopy = newSubscriptionsCopy.filter(
    (copy: ISubscription | undefined) => copy !== undefined
  );

  const subscriptionsList =
    subscriptions && subscriptions.length > 0
      ? [].concat(combinedSubscriptions, filteredCopy as any)
      : newSubscriptions;

  return addPriorityPlaylisting(subscriptionsList);
};
